import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagIcon } from '../icon/icon';
import { ZagButton } from '../button/button';

/**
 * Data passed to the ShiftActionDialog when opened.
 */
export interface ShiftActionDialogData {
  /** The shift name for display */
  shiftName: string;
  /** The shift color */
  shiftColor: string;
  /** Start time (HH:mm) */
  startTime: string;
  /** End time (HH:mm) */
  endTime: string;
  /** Formatted date label (e.g. "Monday, 20.01.2026") */
  dateLabel: string;
  /** Team name */
  teamName: string;
  /** Current swap status (if already marked) */
  currentStatus?: 'swap' | 'takeover' | null;
}

/**
 * Result returned when the dialog is closed with an action.
 */
export interface ShiftActionDialogResult {
  /** The action the user chose */
  action: 'mark-swap' | 'mark-takeover' | 'unmark';
}

/**
 * Dialog that lets an employee mark one of their own shifts as
 * "Looking for Swap" or "Available for Takeover", or remove an
 * existing marking.
 *
 * @example
 * ```typescript
 * const dialogRef = this.dialog.open(ShiftActionDialogComponent, {
 *   data: { shiftName: 'Morning', shiftColor: '#4caf50', ... },
 *   panelClass: 'ai-chat-dialog-panel',
 *   backdropClass: 'ai-chat-dialog-backdrop',
 * });
 *
 * dialogRef.closed.subscribe((result) => {
 *   if (result?.action === 'mark-swap') { ... }
 * });
 * ```
 */
@Component({
  selector: 'zag-shift-action-dialog',
  imports: [TranslatePipe, ZagIcon, ZagButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'shift-action-dialog-host' },
  template: `
    <div class="shift-action-dialog">
      <div class="shift-action-dialog__header">
        <h2 class="shift-action-dialog__title">{{ 'shiftSwaps.actionDialog.title' | translate }}</h2>
        <button type="button" class="shift-action-dialog__close" (click)="onClose()">
          <span zagIcon="close"></span>
        </button>
      </div>

      <div class="shift-action-dialog__body">
        <!-- Shift preview card -->
        <div class="shift-action-dialog__preview">
          <div class="shift-action-dialog__preview-bar" [style.background-color]="data.shiftColor"></div>
          <div class="shift-action-dialog__preview-info">
            <span class="shift-action-dialog__preview-name">{{ data.shiftName }}</span>
            <span class="shift-action-dialog__preview-time">{{ data.startTime }} – {{ data.endTime }}</span>
            <span class="shift-action-dialog__preview-meta">{{ data.dateLabel }} · {{ data.teamName }}</span>
          </div>
        </div>

        @if (data.currentStatus) {
          <!-- Already marked — show current status + unmark option -->
          <div class="shift-action-dialog__status">
            <span zagIcon="info" class="shift-action-dialog__status-icon"></span>
            <span class="shift-action-dialog__status-text">
              {{ 'shiftSwaps.actionDialog.currentlyMarked.' + data.currentStatus | translate }}
            </span>
          </div>

          <div class="shift-action-dialog__actions">
            <button zagButton variant="outlined" (click)="onUnmark()" class="shift-action-dialog__action-btn">
              <span zagIcon="close"></span>
              {{ 'shiftSwaps.actionDialog.unmark' | translate }}
            </button>
          </div>
        } @else {
          <!-- Not marked — offer swap or takeover -->
          <p class="shift-action-dialog__description">
            {{ 'shiftSwaps.actionDialog.description' | translate }}
          </p>

          <div class="shift-action-dialog__actions">
            <button class="shift-action-dialog__option" (click)="onMarkSwap()">
              <span zagIcon="swap_horiz" class="shift-action-dialog__option-icon shift-action-dialog__option-icon--swap"></span>
              <div class="shift-action-dialog__option-text">
                <span class="shift-action-dialog__option-title">{{ 'shiftSwaps.actionDialog.lookingForSwap' | translate }}</span>
                <span class="shift-action-dialog__option-desc">{{ 'shiftSwaps.actionDialog.lookingForSwapDesc' | translate }}</span>
              </div>
              <span zagIcon="chevron_right" class="shift-action-dialog__option-arrow"></span>
            </button>

            <button class="shift-action-dialog__option" (click)="onMarkTakeover()">
              <span zagIcon="person_add" class="shift-action-dialog__option-icon shift-action-dialog__option-icon--takeover"></span>
              <div class="shift-action-dialog__option-text">
                <span class="shift-action-dialog__option-title">{{ 'shiftSwaps.actionDialog.availableForTakeover' | translate }}</span>
                <span class="shift-action-dialog__option-desc">{{ 'shiftSwaps.actionDialog.availableForTakeoverDesc' | translate }}</span>
              </div>
              <span zagIcon="chevron_right" class="shift-action-dialog__option-arrow"></span>
            </button>
          </div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .shift-action-dialog {
        background: var(--surface-color-default);
        border-radius: var(--border-radius-lg);
        padding: var(--spacing-lg);
        max-width: 480px;
        box-shadow: var(--shadow-lg);
      }

      .shift-action-dialog__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--spacing-md);
      }

      .shift-action-dialog__title {
        margin: 0;
        font-size: var(--font-size-lg);
        font-weight: var(--font-weight-semibold);
        color: var(--text-color-primary);
      }

      .shift-action-dialog__close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: none;
        background: transparent;
        color: var(--text-color-secondary);
        cursor: pointer;
        border-radius: var(--border-radius-sm);
        transition: background-color 0.15s;
      }

      .shift-action-dialog__close:hover {
        background: var(--surface-color-hover);
      }

      .shift-action-dialog__body {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
      }

      .shift-action-dialog__preview {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        padding: var(--spacing-sm) var(--spacing-md);
        background: var(--surface-color-subtle);
        border-radius: var(--border-radius-md);
      }

      .shift-action-dialog__preview-bar {
        width: 4px;
        height: 40px;
        border-radius: 2px;
        flex-shrink: 0;
      }

      .shift-action-dialog__preview-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .shift-action-dialog__preview-name {
        font-weight: 600;
        color: var(--text-color-primary);
        font-size: var(--font-size-md);
      }

      .shift-action-dialog__preview-time {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        font-family: monospace;
      }

      .shift-action-dialog__preview-meta {
        font-size: var(--font-size-sm);
        color: var(--text-color-tertiary);
      }

      .shift-action-dialog__status {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        padding: var(--spacing-sm) var(--spacing-md);
        background: color-mix(in srgb, var(--color-info) 10%, transparent);
        border-radius: var(--border-radius-md);
      }

      .shift-action-dialog__status-icon {
        color: var(--color-info);
        font-size: 20px;
        flex-shrink: 0;
      }

      .shift-action-dialog__status-text {
        font-size: var(--font-size-sm);
        color: var(--text-color-primary);
      }

      .shift-action-dialog__description {
        margin: 0;
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        line-height: 1.5;
      }

      .shift-action-dialog__actions {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
      }

      .shift-action-dialog__action-btn {
        width: 100%;
      }

      .shift-action-dialog__option {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        padding: var(--spacing-md);
        background: var(--surface-color-default);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
        cursor: pointer;
        text-align: left;
        transition:
          background-color 0.15s,
          border-color 0.15s;
      }

      .shift-action-dialog__option:hover {
        background: var(--surface-color-hover);
        border-color: var(--border-color-hover);
      }

      .shift-action-dialog__option-icon {
        font-size: 28px;
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--border-radius-md);
      }

      .shift-action-dialog__option-icon--swap {
        background: color-mix(in srgb, var(--color-primary) 12%, transparent);
        color: var(--color-primary);
      }

      .shift-action-dialog__option-icon--takeover {
        background: color-mix(in srgb, var(--color-success) 12%, transparent);
        color: var(--color-success);
      }

      .shift-action-dialog__option-text {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex: 1;
        min-width: 0;
      }

      .shift-action-dialog__option-title {
        font-weight: 500;
        color: var(--text-color-primary);
        font-size: var(--font-size-md);
      }

      .shift-action-dialog__option-desc {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        line-height: 1.4;
      }

      .shift-action-dialog__option-arrow {
        color: var(--text-color-tertiary);
        flex-shrink: 0;
      }
    `,
  ],
})
export class ShiftActionDialogComponent {
  private readonly dialogRef = inject(DialogRef<ShiftActionDialogResult>);
  readonly data = inject<ShiftActionDialogData>(DIALOG_DATA);

  onMarkSwap(): void {
    this.dialogRef.close({ action: 'mark-swap' });
  }

  onMarkTakeover(): void {
    this.dialogRef.close({ action: 'mark-takeover' });
  }

  onUnmark(): void {
    this.dialogRef.close({ action: 'unmark' });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
